import React, { useState } from 'react';
import {
  Shield, ShieldAlert, ShieldCheck, Eye, Swords, AlertTriangle,
  Activity, Clock, TrendingDown, Bug,
} from 'lucide-react';

const SEC_BLUEPRINTS = [
  { id: 'sec-incident-response', name: 'SOAR – Automated Incident Response', description: 'Ingest SIEM alerts, AI triage, contain threats, create ticket, notify SOC, remediate.', icon: ShieldAlert, difficulty: 'advanced', duration: '~2 min auto', category: 'Incident Response', rating: 4.9, usageCount: 2341 },
  { id: 'sec-policy-enforcement', name: 'Security Policy Enforcement', description: 'Assess endpoints against security baseline, detect drift, auto-remediate, report violations.', icon: ShieldCheck, difficulty: 'advanced', duration: '~15 min scan', category: 'Policy', rating: 4.7, usageCount: 876 },
  { id: 'sec-privileged-access-monitoring', name: 'Privileged Access Monitoring', description: 'Monitor Domain Admin group changes, detect anomalous escalations, auto-revert.', icon: Eye, difficulty: 'intermediate', duration: 'Real-time', category: 'Access', rating: 4.8, usageCount: 654 },
  { id: 'sec-automated-remediation', name: 'Automated Threat Remediation', description: 'Multi-step remediation: malware isolation, credential stuffing response, lateral movement containment.', icon: Swords, difficulty: 'advanced', duration: '~3 min', category: 'Remediation', rating: 4.9, usageCount: 1123 },
];

const STATS = [
  { label: 'Open Incidents', value: '7', icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
  { label: 'Alerts Today', value: '143', icon: Activity, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { label: 'Auto-Resolved', value: '98%', icon: TrendingDown, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { label: 'Policy Violations', value: '12', icon: Bug, color: 'text-purple-400', bg: 'bg-purple-500/10' },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'bg-emerald-500/20 text-emerald-300',
  intermediate: 'bg-blue-500/20 text-blue-300',
  advanced: 'bg-purple-500/20 text-purple-300',
};

export function SecurityPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Incident Response', 'Policy', 'Access', 'Remediation'];
  const filtered = activeCategory === 'All' ? SEC_BLUEPRINTS : SEC_BLUEPRINTS.filter((b) => b.category === activeCategory);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Security & Cyber</h1>
          </div>
          <p className="text-gray-500">SOAR, incident response, policy enforcement, and privileged access monitoring</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-xs text-amber-300 font-medium">7 Active Incidents</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Active Incidents */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          Active Incidents
        </h3>
        <div className="space-y-2">
          {[
            { id: 'INC-4821', title: 'Brute force detected – 192.168.1.45', severity: 'HIGH', status: 'Containing', time: '4 min ago' },
            { id: 'INC-4820', title: 'Privileged group change – Domain Admins', severity: 'CRITICAL', status: 'Auto-Reverted', time: '12 min ago' },
            { id: 'INC-4819', title: 'Malware detected on DESKTOP-A4F2', severity: 'HIGH', status: 'Isolating', time: '23 min ago' },
            { id: 'INC-4815', title: 'Policy drift – 3 endpoints OOC', severity: 'MEDIUM', status: 'Auto-Remediated', time: '2 hours ago' },
          ].map((inc) => (
            <div key={inc.id} className="flex items-center gap-3 py-2.5 border-b border-gray-200 last:border-0">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                inc.severity === 'CRITICAL' ? 'bg-red-600/30 text-red-300' :
                inc.severity === 'HIGH' ? 'bg-amber-500/20 text-amber-300' :
                'bg-blue-500/20 text-blue-300'
              }`}>{inc.severity}</span>
              <span className="text-xs text-gray-500 font-mono">{inc.id}</span>
              <span className="text-sm text-white flex-1">{inc.title}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-gray-50 text-gray-400">{inc.status}</span>
              <span className="text-xs text-gray-400">{inc.time}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Security Automations</h2>
          <div className="flex gap-2">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeCategory === cat ? 'bg-orbayx-600/30 text-orbayx-700 border border-orbayx-500/30' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((bp) => {
            const Icon = bp.icon;
            return (
              <div key={bp.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-red-500/30 transition-all group cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-red-400" />
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${DIFFICULTY_COLORS[bp.difficulty]}`}>{bp.difficulty}</span>
                </div>
                <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-red-300 transition-colors">{bp.name}</h3>
                <p className="text-xs text-gray-400 mb-3">{bp.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{bp.duration}</span>
                  <span>⭐ {bp.rating} · {bp.usageCount.toLocaleString()} runs</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
